// Firebase modules are now dynamically imported to eliminate blocking initial page paint.
async function getFirestoreService() {
  const { getFirebaseServices } = await import("./firebase");
  const { db, isConfigured } = await getFirebaseServices();
  if (!isConfigured || !db) {
    throw new Error("Database not connected or configured");
  }
  const firestore = await import("firebase/firestore");
  return { db, ...firestore };
}

async function getStorageService() {
  const { getFirebaseServices } = await import("./firebase");
  const { storage } = await getFirebaseServices();
  if (!storage) {
    throw new Error("Storage not configured");
  }
  const storageLib = await import("firebase/storage");
  return { storage, ...storageLib };
}
import type { Product, Combo, HeroContent, PromoContent, CommitmentContent, Review, AboutContent } from "@/types/content";

export const DEFAULT_PRODUCTS: Product[] = [
  {
    name: "Ice White Puffer",
    description:
      "A premium, minimalist white puffer jacket. Exceptionally warm, highly breathable, and tailored for a perfect oversized fit. Built for the modern winter.",
    price: 3950,
    image: "/images/products/product-1.webp",
    tag: "Signature Edition",
    order: 0,
  },
  {
    name: "Tie-Dye Cloud Jacket",
    description:
      "Unique tie-dye patterned puffer with a soft gradient finish. Double-lined hood and a clean, cropped silhouette for ultimate street style.",
    price: 4200,
    image: "/images/products/product-2.webp",
    tag: "Premium Quality",
    order: 1,
  },
  {
    name: "Midnight Black Down",
    description:
      "Classic black corduroy-style puffer. Features a high collar, thick insulation, and a sleek, versatile design that goes with everything.",
    price: 3450,
    image: "/images/products/product-3.webp",
    tag: "Best Seller",
    order: 2,
  },
  {
    name: "Deep Purple NotraClo",
    description:
      "Vibrant purple heavyweight down jacket. Designed for extreme cold while maintaining a striking, high-fashion aesthetic.",
    price: 4500,
    image: "/images/products/product-4.webp",
    tag: "Exclusive",
    order: 3,
  },
];

export const DEFAULT_COMBOS: Combo[] = [
  {
    name: "Minimalist Essentials Set",
    items: [
      "Classic Premium Cotton T-Shirt (1 pc)",
      "Handcrafted Slim Leather Wallet (1 pc)",
      "Organic Cotton Daily Tote Bag (1 pc)",
    ],
    price: 2499,
    originalPrice: 2750,
    badge: "Save 9%",
    image: "https://images.unsplash.com/photo-1523381210434-271e8be1f52b?q=80&w=800&auto=format&fit=crop",
    order: 0,
  },
  {
    name: "Urban Comfort Pack",
    items: [
      "Minimalist Loopback Hoodie (1 pc)",
      "Classic Premium Cotton T-Shirt (2 pcs)",
      "Organic Cotton Daily Tote Bag (1 pc)",
    ],
    price: 3599,
    originalPrice: 4100,
    badge: "Save 12%",
    image: "https://images.unsplash.com/photo-1479064555552-3ef4979f8908?q=80&w=800&auto=format&fit=crop",
    order: 1,
  },
];

export const DEFAULT_HERO: HeroContent = {
  titleLine1: "Crafted for Comfort,",
  titleHighlight: "Designed for",
  titleLine2: "Everyday Wear",
  subtitle:
    "We create high-quality, sustainable, and minimalistic everyday essentials designed to fit perfectly and stand the test of time.",
  heroImageUrls: [
    "https://images.unsplash.com/photo-1523381210434-271e8be1f52b?q=80&w=1600&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1479064555552-3ef4979f8908?q=80&w=1600&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?q=80&w=1600&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?q=80&w=1600&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?q=80&w=1600&auto=format&fit=crop"
  ],
  ctaText: "Explore Collection",
};

export const DEFAULT_PROMO: PromoContent = {
  badge: "🎁 Special Offer",
  titleLine1: "Free Delivery",
  titleHighlight: "On Orders Over",
  titleLine2: "৳2000",
  subtitle: "Order today and receive your premium everyday essentials right at your doorstep — with completely free delivery inside Dhaka.",
  ctaText: "Shop Now",
  bgImageUrl: "https://images.unsplash.com/photo-1483985988355-763728e1935b?q=80&w=1600&auto=format&fit=crop",
  items: [
    { icon: "👕", title: "Premium T-Shirt", sub: "100% Organic Cotton", price: "৳950" },
    { icon: "🧥", title: "Comfort Hoodie", sub: "Heavyweight Terry", price: "৳1,850" },
    { icon: "💼", title: "Leather Wallet", sub: "Full Grain Leather", price: "৳1,450" },
    { icon: "👜", title: "Canvas Tote", sub: "Durable & Washable", price: "৳350" },
  ]
};

export const DEFAULT_COMMITMENT: CommitmentContent = {
  badge: "OUR COMMITMENT",
  titleLine1: "TRUSTED BY",
  titleHighlight: "10,000+",
  titleLine2: "HAPPY CUSTOMERS",
  description: "We are committed to sourcing only the finest materials and supporting fair, ethical manufacturing. Every piece in our collection goes through rigorous quality control before it arrives at your doorstep.",
  bgImageUrl: "https://images.unsplash.com/photo-1507679799987-c73779587ccf?q=80&w=1600&auto=format&fit=crop",
  stats: [
    { value: "10,000+", label: "Happy Customers" },
    { value: "100%", label: "Organic Cotton" },
    { value: "4.9★", label: "Average Rating" },
  ],
  deliveryTitle: "Fast Delivery",
  deliverySub: "Dhaka: 24 Hours",
  paymentTitle: "Cash on Delivery",
  paymentSub: "Pay after receiving"
};

export const DEFAULT_REVIEWS: Review[] = [
  {
    name: "Sumaiya Akter",
    content: "Outstanding quality! The t-shirt is incredibly soft and holds its shape perfectly even after multiple washes.",
    image: ""
  },
  {
    name: "Rahat Hossain",
    content: "Super fast delivery and the genuine leather wallet is beautiful. Highly recommend this brand!",
    image: ""
  },
  {
    name: "Nusrat Jahan",
    content: "The heavy-weight hoodie is extremely cozy and fits perfectly. Excellent value for money.",
    image: ""
  }
];

export const DEFAULT_ABOUT: AboutContent = {
  hero: {
    line1: "CRAFTED WITH CARE,",
    highlight: "DESIGNED FOR",
    line2: "EVERYDAY COMFORT.",
    description: "In 2022, we set out with a simple vision: to create a premium, minimalist brand that makes high-quality everyday essentials affordable and sustainably made.",
    bgImage: "https://images.unsplash.com/photo-1507679799987-c73779587ccf?q=80&w=1600&auto=format&fit=crop"
  },
  ticker: ["100% Organic Cotton", "Sustainably Sourced", "Ethical Production", "Premium Craftsmanship", "Minimalist Aesthetics", "Premium Durability"],
  origin: {
    year: "2022",
    title: "A Commitment to Quality.",
    p1: "We noticed a persistent problem in everyday apparel: products were either cheap and poorly constructed, or high-quality but marked up excessively.",
    p2: "We spent six months sourcing the finest organic long-staple cotton and working directly with ethical local workshops to craft everyday items that stand the test of time.",
    p3: "Today, we are proud to serve over 10,000 customers who appreciate the finer details, minimalist design, and durable quality.",
    quote: "If it's not made to last, we don't make it.",
    quoteAuthor: "FOUNDER",
    image: "https://images.unsplash.com/photo-1521572267360-ee0c2909d518?q=80&w=800&auto=format&fit=crop"
  },
  stats: {
    families: 10000,
    districts: 64,
    safetyChecks: 15,
    years: 4
  },
  values: [
    { title: "Uncompromising Quality", desc: "We use only premium materials and check every product thoroughly before delivery." },
    { title: "Ethical & Sustainable", desc: "We prioritize eco-friendly organic fibers and work exclusively with fair factories." },
    { title: "Fair Pricing Structure", desc: "By selling directly to you, we eliminate high retail markups." },
    { title: "Durability by Design", desc: "Our products are constructed to resist shrinking, fading, and wearing out." }
  ],
  promise: {
    title: "We only craft products that we would proudly wear ourselves and share with our friends.",
    bgImage: "https://images.unsplash.com/photo-1483985988355-763728e1935b?q=80&w=1600&auto=format&fit=crop"
  }
};

/* ——— Caching Helpers for Stale-While-Revalidate ——— */
function getCached<T>(key: string, defaultValue: T): T {
  try {
    const cached = localStorage.getItem(`cache_${key}`);
    return cached ? JSON.parse(cached) : defaultValue;
  } catch {
    return defaultValue;
  }
}

function setCache<T>(key: string, data: T): void {
  try {
    localStorage.setItem(`cache_${key}`, JSON.stringify(data));
  } catch (err) {
    console.error("Cache set failed:", err);
  }
}

function clearCache(key: string): void {
  try {
    localStorage.removeItem(`cache_${key}`);
  } catch (err) {
    console.error("Cache clear failed:", err);
  }
}

// ——— Firestore read ———
export async function getProducts(onUpdate?: (products: Product[]) => void): Promise<Product[]> {
  const cached = getCached("products", DEFAULT_PRODUCTS);
  setTimeout(async () => {
    try {
      const { db, collection, getDocs } = await getFirestoreService();
      const snap = await getDocs(collection(db, "products"));
      if (!snap.empty) {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() } as Product));
        list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        setCache("products", list);
        if (onUpdate) onUpdate(list);
      }
    } catch (err) {
      console.warn("Firestore getProducts failed:", err);
    }
  }, 3000);
  return cached;
}

export async function getCombos(onUpdate?: (combos: Combo[]) => void): Promise<Combo[]> {
  const cached = getCached("combos", DEFAULT_COMBOS);
  setTimeout(async () => {
    try {
      const { db, collection, getDocs } = await getFirestoreService();
      const snap = await getDocs(collection(db, "combos"));
      if (!snap.empty) {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() } as Combo));
        list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        setCache("combos", list);
        if (onUpdate) onUpdate(list);
      }
    } catch (err) {
      console.warn("Firestore getCombos failed:", err);
    }
  }, 3000);
  return cached;
}

export async function getHeroContent(onUpdate?: (hero: HeroContent) => void): Promise<HeroContent> {
  const cached = getCached("hero", DEFAULT_HERO);
  setTimeout(async () => {
    try {
      const { db, doc, getDoc } = await getFirestoreService();
      const snap = await getDoc(doc(db, "content", "hero"));
      if (snap.exists()) {
        const data = snap.data();
        let urls = data.heroImageUrls || [];
        if (urls.length === 0 && data.heroImageUrl) {
          urls = [data.heroImageUrl];
        }
        const heroData = { ...DEFAULT_HERO, ...data, heroImageUrls: urls };
        setCache("hero", heroData);
        if (onUpdate) onUpdate(heroData);
      }
    } catch (err) {
      console.warn("Firestore getHeroContent failed:", err);
    }
  }, 3000);
  return cached;
}

export async function getPromoContent(onUpdate?: (promo: PromoContent) => void): Promise<PromoContent> {
  const cached = getCached("promo", DEFAULT_PROMO);
  setTimeout(async () => {
    try {
      const { db, doc, getDoc } = await getFirestoreService();
      const snap = await getDoc(doc(db, "content", "promo"));
      if (snap.exists()) {
        const promoData = { ...DEFAULT_PROMO, ...snap.data() } as PromoContent;
        setCache("promo", promoData);
        if (onUpdate) onUpdate(promoData);
      }
    } catch (err) {
      console.warn("Firestore getPromoContent failed:", err);
    }
  }, 3000);
  return cached;
}

export async function getCommitmentContent(onUpdate?: (commitment: CommitmentContent) => void): Promise<CommitmentContent> {
  const cached = getCached("commitment", DEFAULT_COMMITMENT);
  setTimeout(async () => {
    try {
      const { db, doc, getDoc } = await getFirestoreService();
      const snap = await getDoc(doc(db, "content", "commitment"));
      if (snap.exists()) {
        const commitmentData = { ...DEFAULT_COMMITMENT, ...snap.data() } as CommitmentContent;
        setCache("commitment", commitmentData);
        if (onUpdate) onUpdate(commitmentData);
      }
    } catch (err) {
      console.warn("Firestore getCommitmentContent failed:", err);
    }
  }, 3000);
  return cached;
}

export async function getReviews(onUpdate?: (reviews: Review[]) => void): Promise<Review[]> {
  const cached = getCached("reviews", DEFAULT_REVIEWS);
  setTimeout(async () => {
    try {
      const { db, doc, getDoc } = await getFirestoreService();
      const snap = await getDoc(doc(db, "content", "reviews"));
      if (snap.exists()) {
        const data = snap.data();
        const reviewsData = data.reviews || DEFAULT_REVIEWS;
        setCache("reviews", reviewsData);
        if (onUpdate) onUpdate(reviewsData);
      }
    } catch (err) {
      console.warn("Firestore getReviews failed:", err);
    }
  }, 3000);
  return cached;
}

export async function getAboutContent(onUpdate?: (about: AboutContent) => void): Promise<AboutContent> {
  const cached = getCached("about", DEFAULT_ABOUT);
  setTimeout(async () => {
    try {
      const { db, doc, getDoc } = await getFirestoreService();
      const snap = await getDoc(doc(db, "content", "about"));
      if (snap.exists()) {
        const aboutData = snap.data() as AboutContent;
        setCache("about", aboutData);
        if (onUpdate) onUpdate(aboutData);
      }
    } catch (err) {
      console.warn("Firestore getAboutContent failed:", err);
    }
  }, 3000);
  return cached;
}

// ——— Firestore write (admin) ———
export async function saveProduct(product: Product): Promise<void> {
  const { db, doc, updateDoc, addDoc, collection } = await getFirestoreService();
  const { id, ...data } = product;
  const formattedData: any = {
    ...data,
    price: Number(data.price) || 0,
    originalPrice: data.originalPrice ? Number(data.originalPrice) : 0,
    order: Number(data.order) || 0,
  };

  Object.keys(formattedData).forEach(key => {
    if (formattedData[key] === undefined) {
      delete formattedData[key];
    }
  });

  try {
    if (id) {
      await updateDoc(doc(db, "products", id), formattedData as any);
    } else {
      await addDoc(collection(db, "products"), formattedData);
    }
    clearCache("products");
  } catch (err: any) {
    console.error("Firestore saveProduct failed:", err);
    throw err;
  }
}

export async function deleteProduct(id: string): Promise<void> {
  try {
    const { db, doc, deleteDoc } = await getFirestoreService();
    await deleteDoc(doc(db, "products", id));
    clearCache("products");
  } catch (err) {
    console.error("Firestore deleteProduct failed:", err);
    throw err;
  }
}

export async function saveCombo(combo: Combo): Promise<void> {
  const { db, doc, updateDoc, addDoc, collection } = await getFirestoreService();
  const { id, ...data } = combo;
  const formattedData: any = {
    ...data,
    price: Number(data.price) || 0,
    originalPrice: Number(data.originalPrice) || 0,
    order: Number(data.order) || 0,
    items: Array.isArray(data.items) ? data.items.filter(i => i.trim() !== "") : [],
  };

  Object.keys(formattedData).forEach(key => {
    if (formattedData[key] === undefined) {
      delete formattedData[key];
    }
  });

  try {
    if (id) {
      await updateDoc(doc(db, "combos", id), formattedData as any);
    } else {
      await addDoc(collection(db, "combos"), formattedData);
    }
    clearCache("combos");
  } catch (err: any) {
    console.error("Firestore saveCombo failed:", err);
    throw err;
  }
}

export async function deleteCombo(id: string): Promise<void> {
  try {
    const { db, doc, deleteDoc } = await getFirestoreService();
    await deleteDoc(doc(db, "combos", id));
    clearCache("combos");
  } catch (err) {
    console.error("Firestore deleteCombo failed:", err);
    throw err;
  }
}

export async function saveHeroContent(hero: HeroContent): Promise<void> {
  try {
    const { db, doc, setDoc } = await getFirestoreService();
    await setDoc(doc(db, "content", "hero"), JSON.parse(JSON.stringify(hero)));
    clearCache("hero");
  } catch (err: any) {
    console.error("Firestore saveHeroContent failed:", err);
    throw err;
  }
}

export async function savePromoContent(promo: PromoContent): Promise<void> {
  try {
    const { db, doc, setDoc } = await getFirestoreService();
    await setDoc(doc(db, "content", "promo"), JSON.parse(JSON.stringify(promo)));
    clearCache("promo");
  } catch (err: any) {
    console.error("Firestore savePromoContent failed:", err);
    throw err;
  }
}

export async function saveCommitmentContent(commitment: CommitmentContent): Promise<void> {
  try {
    const { db, doc, setDoc } = await getFirestoreService();
    await setDoc(doc(db, "content", "commitment"), JSON.parse(JSON.stringify(commitment)));
    clearCache("commitment");
  } catch (err: any) {
    console.error("Firestore saveCommitmentContent failed:", err);
    throw err;
  }
}

export async function saveReviews(reviews: Review[]): Promise<void> {
  try {
    const { db, doc, setDoc } = await getFirestoreService();
    await setDoc(doc(db, "content", "reviews"), JSON.parse(JSON.stringify({ reviews })));
    clearCache("reviews");
  } catch (err: any) {
    console.error("Firestore saveReviews failed:", err);
    throw err;
  }
}

export async function saveAboutContent(content: AboutContent): Promise<void> {
  try {
    const { db, doc, setDoc } = await getFirestoreService();
    await setDoc(doc(db, "content", "about"), JSON.parse(JSON.stringify(content)));
    clearCache("about");
  } catch (err: any) {
    console.error("Firestore saveAboutContent failed:", err);
    throw err;
  }
}

// ——— Storage upload ———
export async function uploadImage(
  path: string,
  file: File
): Promise<string> {
  const { storage, ref, uploadBytes, getDownloadURL } = await getStorageService();
  const storageRef = ref(storage, path);

  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => {
      reject(new Error("Timeout! Firebase Storage is not responding."));
    }, 15000);
  });

  try {
    await Promise.race([
      uploadBytes(storageRef, file),
      timeoutPromise
    ]);
    const url = await getDownloadURL(storageRef);
    return url;
  } catch (err) {
    console.error("Upload error in content.ts:", err);
    throw err;
  }
}

// ——— Write Order ———
export async function saveOrder(orderData: any): Promise<void> {
  try {
    const { db, collection, addDoc } = await getFirestoreService();
    await addDoc(collection(db, "orders"), {
      ...orderData,
      createdAt: new Date().toISOString()
    });
  } catch (err) {
    console.error("Failed to save order to Firebase:", err);
  }
}

export async function getOrders(): Promise<any[]> {
  try {
    const { db, collection, getDocs } = await getFirestoreService();
    const snap = await getDocs(collection(db, "orders"));
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    list.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return list;
  } catch {
    return [];
  }
}

export async function deleteOrder(id: string): Promise<void> {
  try {
    const { db, doc, deleteDoc } = await getFirestoreService();
    await deleteDoc(doc(db, "orders", id));
  } catch (err) {
    console.error("Firestore deleteOrder failed:", err);
    throw err;
  }
}


